import { useEffect, useState } from "react";

import { ChevronRight } from "lucide-react";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";

import { cn } from "@/lib/utils";
import { Button } from "../ui/button";

const Navbar = ({ currentPage = "/" }: { currentPage: string }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openSubDropdown, setOpenSubDropdown] = useState<string | null>(null);

  const pathname = currentPage;

  const navLinks = [
    {
      label: "Funktionen",
      href: "#features",
      dropdownItems: [
        {
          title: "YellowTime GmbH",
          subGroup: {
            item: [
              {
                href: "/features/we-are",
                description: "Wer sind wir?",
              },
            ],
          },
        },
        {
          title: "YellowTime® Web",
          subGroup: {
            item: [
              {
                href: "/features/reports",
                description: "Berichte",
              },
              {
                href: "/features/contracts",
                description: "Große Projekte und Arbeitsverwaltung",
              },
              {
                href: "/features/employees",
                description: "Mitarbeitermanagment",
              },
              {
                href: "/features/gps",
                description: "GPS-Tracking",
              },
              {
                href: "/features/extras",
                description: "Urlaube, Nachrichten, Berechtigungen",
              },
            ],
          },
        },
        {
          title: "YellowTime® App",
          subGroup: {
            item: [
              {
                href: "/features/app-task",
                description: "Aufgabenübersicht und Details",
              },
              {
                href: "/features/nfc",
                description: "Fälschungssicher & Transparent",
              },
              {
                href: "/features/messages",
                description: "Urlaubsantrag",
              },
              {
                href: "/features/leaves",
                description: "Nachrichten",
              },           
            ],
          },
        },
      ],
    },
    { label: "Preise", href: "/pricing" },
    { label: "Über uns", href: "/about" },
    { label: "Blog", href: "/blog" },
    { label: "Kontakt", href: "/contact" },
    
  ];

  return (
    <header className="relative z-50 border-b backdrop-blur-sm">
      <div className="container">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-1">
            <img
              src="/images/yt-long-logo.png"
              alt="logo"
              width={130}
              height={100}
            />  
          </a>

          {/* Desktop Navigation */}
          <NavigationMenu className="hidden items-center gap-8 lg:flex">
            <NavigationMenuList>
              {navLinks.map((link) =>
                link.dropdownItems ? (
                  <NavigationMenuItem key={link.label}>
                    <NavigationMenuTrigger className="text-primary lg:text-base">
                      {link.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="flex w-[700px] gap-x-8 p-4">
                        {(() => {
                          const half = Math.ceil(link.dropdownItems.length / 2);
                          const firstHalf = link.dropdownItems.slice(0, half);
                          const secondHalf = link.dropdownItems.slice(half);

                          return (
                            <>
                              <li className="flex w-1/2 flex-col gap-y-6">
                                {firstHalf.map((item) => (
                                  <div key={item.title} className="space-y-2">
                                    <div className="text-sm font-medium">
                                      {item.title}
                                    </div>
                                    <ul className="space-y-1">
                                      {item.subGroup.item.map(
                                        (subItem, index) => (
                                          <li key={index}>
                                            <NavigationMenuLink asChild>
                                              <a
                                                href={subItem.href}
                                                className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block rounded-md p-2 text-sm transition-colors"
                                                style={{ maxWidth: "300px" }}
                                              >
                                                <p className="text-muted-foreground line-clamp-2 leading-snug">
                                                  {subItem.description}
                                                </p>
                                              </a>
                                            </NavigationMenuLink>
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  </div>
                                ))}
                              </li>
                              <li className="flex w-1/2 flex-col gap-y-6">
                                {secondHalf.map((item) => (
                                  <div key={item.title} className="space-y-2">
                                    <div className="text-sm font-medium">
                                      {item.title}
                                    </div>
                                    <ul className="space-y-1">
                                      {item.subGroup.item.map(
                                        (subItem, index) => (
                                          <li key={index}>
                                            <NavigationMenuLink asChild>
                                              <a
                                                href={subItem.href}
                                                className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block rounded-md p-2 text-sm transition-colors"
                                                style={{ maxWidth: "300px" }}
                                              >
                                                <p className="text-muted-foreground line-clamp-2 leading-snug">
                                                  {subItem.description}
                                                </p>
                                              </a>
                                            </NavigationMenuLink>
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  </div>
                                ))}
                              </li>
                            </>
                          );
                        })()}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                ) : (
                  <NavigationMenuItem key={link.label}>
                    <a
                      href={link.href}
                      className={cn(
                        navigationMenuTriggerStyle(),
                        "text-primary lg:text-base",
                        pathname === link.href && "text-muted-foreground",
                      )}
                    >
                      {link.label}
                    </a>
                  </NavigationMenuItem>
                ),
              )}
            </NavigationMenuList>
          </NavigationMenu>

          {/* Demo Button */}
          <div className="flex items-center gap-2.5">
            <div
              className={`transition-opacity duration-300 ${isMenuOpen ? "pointer-events-none opacity-0" : "opacity-100"}`}
            ></div>
            <a href="/register" className="hidden lg:block">
              <Button
                variant="outline"
                className="gap-1 bg-yellow-300 hover:bg-yellow-300"
              >
                Beta Testen
                <ChevronRight className="size-4" />
              </Button>
            </a>

            {/* Hamburger Menu Button (Mobile Only) */}
            <button
              className="text-muted-foreground relative flex h-8 w-8 lg:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              <div className="absolute left-1/2 top-1/2 block w-[18px] -translate-x-1/2 -translate-y-1/2 transform">
                <span
                  aria-hidden="true"
                  className={`absolute block h-0.5 w-full transform rounded-full bg-current transition duration-500 ease-in-out ${isMenuOpen ? "rotate-45" : "-translate-y-1.5"}`}
                ></span>
                <span
                  aria-hidden="true"
                  className={`absolute block h-0.5 w-full transform rounded-full bg-current transition duration-500 ease-in-out ${isMenuOpen ? "opacity-0" : ""}`}
                ></span>
                <span
                  aria-hidden="true"
                  className={`absolute block h-0.5 w-full transform rounded-full bg-current transition duration-500 ease-in-out ${isMenuOpen ? "-rotate-45" : "translate-y-1.5"}`}
                ></span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`bg-background container fixed inset-0 top-full flex h-[calc(100vh-64px)] flex-col transition-all duration-300 ease-in-out lg:hidden ${
          isMenuOpen
            ? "visible translate-x-0 opacity-100"
            : "invisible translate-x-full opacity-0"
        }`}
      >
        <nav className="mt-3 flex flex-1 flex-col gap-6 overflow-y-auto">
          {navLinks.map((link) =>
            link.dropdownItems ? (
              <div key={link.label}>
                {/* Main Nav Label */}
                <button
                  onClick={() =>
                    setOpenDropdown(
                      openDropdown === link.label ? null : link.label,
                    )
                  }
                  className="text-primary flex w-full items-center justify-between text-lg font-medium tracking-[-0.36px]"
                >
                  {link.label}
                  <ChevronRight
                    className={cn(
                      "h-4 w-4 transition-transform",
                      openDropdown === link.label ? "rotate-90" : "",
                    )}
                  />
                </button>

                <div
                  className={cn(
                    "ml-4 overflow-hidden transition-all",
                    openDropdown === link.label
                      ? "max-h-[1000px] opacity-100"
                      : "max-h-0 opacity-0",
                  )}
                >
                  {/* Now inside, map each dropdownItem as a separate dropdown */}
                  {link.dropdownItems.map((item) => (
                    <div key={item.title} className="mt-3">
                      <button
                        onClick={() =>
                          setOpenSubDropdown(
                            openSubDropdown === item.title ? null : item.title,
                          )
                        }
                        className="text-primary flex w-full items-center justify-between text-base font-medium"
                      >
                        {item.title}
                        <ChevronRight
                          className={cn(
                            "h-4 w-4 transition-transform",
                            openSubDropdown === item.title ? "rotate-90" : "",
                          )}
                        />
                      </button>

                      <div
                        className={cn(
                          "ml-4 mt-2 space-y-2 overflow-hidden transition-all",
                          openSubDropdown === item.title
                            ? "max-h-[1000px] opacity-100"
                            : "max-h-0 opacity-0",
                        )}
                      >
                        {item.subGroup.item.map((subItem, index) => (
                          <a
                            key={index}
                            href={subItem.href}
                            className="hover:bg-accent flex items-start gap-3 rounded-md p-2"
                            onClick={() => {
                              setIsMenuOpen(false);
                              setOpenDropdown(null);
                              setOpenSubDropdown(null);
                            }}
                          >
                            <p className="text-muted-foreground text-sm">
                              {subItem.description}
                            </p>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className={cn(
                  "text-primary text-lg tracking-[-0.36px]",
                  pathname === link.href && "text-muted-foreground",
                )}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </a>
            ),
          )}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
